/* eslint-disable no-nested-ternary */
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';

import { dateTimeFormat } from 'constants/formats';
import ToggleMenu from 'modules/_shared/components/ToggleMenu';

import { Communication } from 'types';

import { formatNumber, formatNumberDecimals } from 'utils/formats';
import { useCommunicationsPage } from '../context/CommunicationsPageContext';

const statusConfig: Record<string, { color: string; label: string }> = {
  delivered: { color: '#34d399', label: 'Delivered' },
  sent: { color: '#60a5fa', label: 'Sent' },
  bounced: { color: '#f87171', label: 'Bounced' },
  spam: { color: '#fbbf24', label: 'Spam' },
  delayed: { color: '#fb923c', label: 'Delayed' },
  rejected: { color: '#ef4444', label: 'Rejected' },
  failed: { color: '#ef4444', label: 'Failed' },
};

type CommunicaionsListRowProps = {
  index: number;
  communication: Communication;
};

const CommunicaionsListRow: FC<CommunicaionsListRowProps> = ({
  index,
  communication,
}) => {
  const { t } = useTranslate();
  const { data, actions } = useCommunicationsPage();

  const recipientsCount: Record<string, number> =
    communication?.recipientsCount || {};

  const activeStatuses = Object.entries(statusConfig)
    .filter(([key]) => recipientsCount?.[key] > 0)
    .map(([key, config]) => ({
      key,
      count: recipientsCount?.[key] || 0,
      ...config,
    }));

  const handleOpenCommunication = () => {
    actions.handleOpenCommunication(index);
  };

  const handleDeleteCommunication = () => {
    actions.handleDeleteCommunication(index);
  };

  const deliveredPercentage = recipientsCount?.delivered
    ? (recipientsCount?.delivered / recipientsCount?.total) * 100
    : 0;

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`communication-view-option-${index}`}
      onClick={handleOpenCommunication}
    >
      <em className="icon ni ni-eye fs-20px" />
      <span className="ml-1">{t('See')}</span>
    </a>,
    <a
      className="d-flex cursor-pointer"
      key={`communication-delete-option-${index}`}
      onClick={handleDeleteCommunication}
    >
      <em className="icon ni ni-trash fs-20px" />
      <span className="ml-1">{t('Delete')}</span>
    </a>,
  ];

  const hasAttachments =
    communication?.attachments && communication.attachments.length > 0;

  return (
    <div
      className={`nk-ibx-item ${communication?.isUnread ? 'is-unread' : ''}`}
      key={communication._id}
    >
      <div className="nk-ibx-item-elem nk-ibx-item-star d-none d-lg-flex">
        <Link
          to={`/detalle-comunicacion/${communication?.society}/${communication._id}/certificacion`}
          className="btn btn-icon btn-trigger"
        >
          <em className="icon ni ni-shield-check-fill text-soft" />
        </Link>
      </div>

      <div className="comm-row__dot-col">
        {communication?.isUnread && <span className="comm-row__unread-dot" />}
      </div>

      <div
        className="nk-ibx-item-elem nk-ibx-item-fluid d-flex flex-column"
        onClick={handleOpenCommunication}
      >
        <div className="nk-ibx-context-group">
          <div className="nk-ibx-context">
            <span className="nk-ibx-context-text">
              <span className="heading mr-1">{communication?.subject}</span>

              <span className="d-inline d-lg-inline">
                {communication?.bodySummary || ''}
              </span>
            </span>
          </div>
        </div>

        {activeStatuses.length > 0 && (
          <div className="comm-row__stats">
            {activeStatuses.map((status, i) => (
              <span key={status.key} className="comm-row__stat">
                <span style={{ color: status.color }}>
                  {formatNumber(status.count)} {t(status.label)}
                </span>
                {i < activeStatuses.length - 1 && (
                  <span className="comm-row__separator">·</span>
                )}
              </span>
            ))}

            <span className="comm-row__delivery-bar">
              <span className="comm-row__delivery-bar-track">
                <span
                  className="comm-row__delivery-bar-fill"
                  style={{
                    width: `${deliveredPercentage}%`,
                  }}
                />
              </span>
              <span className="comm-row__delivery-bar-label">
                {formatNumberDecimals(deliveredPercentage)}%
              </span>
            </span>
          </div>
        )}
      </div>

      <div
        className="nk-ibx-item-elem nk-ibx-item-attach d-none d-lg-flex align-items-center"
        onClick={handleOpenCommunication}
      >
        {hasAttachments && <em className="icon ni ni-clip text-soft" />}
      </div>

      <div className="d-none d-lg-flex nk-ibx-item-elem nk-ibx-item-time">
        <div className="sub-text">
          {communication?.date
            ? format(new Date(communication.date), dateTimeFormat)
            : '-'}
        </div>
      </div>

      {!data.isReadOnly && <ToggleMenu toggleOptions={toggleOptions} />}
    </div>
  );
};

export default CommunicaionsListRow;
